import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@pf/contracts';
import { useAuth } from '@/auth/auth-context';
import { EmptyState } from '@/components/ui/feedback';
import type { ConsoleArea } from '@/lib/api/console-contracts';

/**
 * Route guards.
 *
 * The guard is on **area**, not on a URL prefix, and area is a property of the signed-in identity
 * rather than a choice at the login form. A customer who types `/ops/dashboard` is sent back to
 * the cashier; an operator who types `/app/deposit` is sent to the dashboard. That is the whole
 * point of D-I7 — two products, one shell — expressed as a rule rather than as a convention.
 *
 * `RequireRole` layers SAD §11's RBAC on top for operator screens: Viewer can read everything,
 * Maker can propose, Checker can confirm, Admin can do both plus directory work.
 */

/** Full-page spinner while the session probe is in flight. */
function SessionPending(): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      <span className="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        {t('common.restoringSession')}
      </span>
    </div>
  );
}

/** Render `children` only for a session in `area`; otherwise redirect. */
export function RequireArea({
  area,
  children,
}: {
  /** The area this subtree belongs to. */
  area: ConsoleArea;
  /** Guarded subtree. */
  children: ReactNode;
}): ReactNode {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();

  const mismatch = session !== null && session.area !== area;
  const missing = !isLoading && session === null;

  useEffect(() => {
    if (missing) void navigate({ to: '/login', replace: true });
    else if (mismatch) {
      void navigate({
        to: session?.area === 'operator' ? '/ops/dashboard' : '/app/deposit',
        replace: true,
      });
    }
  }, [missing, mismatch, navigate, session?.area]);

  if (isLoading || session === null || mismatch) return <SessionPending />;
  return <>{children}</>;
}

/** Render `children` only when the operator holds `role`, otherwise explain why not. */
export function RequireRole({
  role,
  children,
  fallback,
}: {
  /** Minimum role. */
  role: Role;
  /** Guarded subtree. */
  children: ReactNode;
  /** Rendered instead of the default explanation. */
  fallback?: ReactNode | undefined;
}): ReactNode {
  const { can, session } = useAuth();
  const { t } = useTranslation();
  if (can(role)) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return (
    <EmptyState
      title={t('common.requireRoleTitle', { role: role.toLowerCase() })}
      description={t('common.requireRoleDescription', {
        currentRole: session?.role ?? t('common.unknownRole'),
        role: role.toLowerCase(),
      })}
    />
  );
}
